import {
  all,
  takeEvery,
  takeLatest,
  put,
  call,
  fork,
} from 'redux-saga/effects';
import { startSubmit, stopSubmit } from 'redux-form';
import { push } from 'connected-react-router';
import {
  inviteTeamsActionTypes,
  manageTeamMembersActionTypes,
  manageTeamsActionTypes,
} from './ManageTeams.actionTypes';
import teamService from '../../services/team';
import manageTeamsService from '../../services/manageTeams';
import {
  addNotification,
  notificationStates,
} from '../../components/Notification';
import firebase from '../../config/firebase.config';
const database = firebase.firestore();

// returns the finalize data
function getFinalizeAdminData(existedUsers, nonExistedEmails, teams, sender) {
  let finalAdminData = [];
  let finalInviteData = [];

  let nonExistAdmins = nonExistedEmails.map((neEmail) => ({
    email: neEmail,
    registered: false,
    eligibleFor: [...teams],
  }));

  finalAdminData = [...nonExistAdmins, ...existedUsers];

  const invitedTo = teams.map((tm) => ({
    teamId: tm.value,
    teamName: tm.label,
    invitedAs: 'Admin',
  }));

  finalAdminData.map((ad) => {
    if (ad.eligibleFor.length > 0) {
      finalInviteData.push({
        invitedBy: {
          emailId: sender.email,
          fullName: sender.name,
          companyDetails: { ...sender.companyDetails },
        },
        invitedTo: [...invitedTo],
        invitedAs: 'Admin',
        userDetails: {
          email: ad.email,
          registered: ad.registered,
        },
        accepted: false,
      });
    }
  });

  return [...finalInviteData];
}

function* sendInviteSaga({ payload }) {
  yield put({ type: inviteTeamsActionTypes.SEND_INVITE_PENDING });
  try {
    let { userType, emails, memberOfTeams, managingTeams, sender, projects } =
      payload;
    if (['Client'].includes(userType)) {
      let allEmails = emails.map((em) => em.toLowerCase());
      // Existed users on the platform
      let existedUsers = yield call(teamService.getAllMembers, allEmails);
      let existedEmails = existedUsers.map((usr) => usr.accountEmailid);
      // Non-existed users on the platform
      let nonExistedEmails = allEmails.filter(
        (em) => !existedEmails.includes(em)
      );
      // finalizes the data to add into the inviteDatabase
      let finalizeInvitationData = [];
      if (userType === 'Client') {
        finalizeInvitationData = yield call(
          teamService.getFinalizedProjectInvitation,
          existedUsers,
          allEmails,
          projects,
          sender,
          userType
        );
      }
      if (finalizeInvitationData) {
        const sendInvitesResponse = yield call(
          [teamService, teamService.addInvitesToDatabase],
          finalizeInvitationData
        );
        if (sendInvitesResponse.success) {
          const messages = sendInvitesResponse.data || [];

          // Show individual toast messages for each email
          for (const msg of messages) {
            addNotification({
              message: `${msg.email}: ${msg.message}`,
              level: msg.message.toLowerCase().includes('sent')
                ? notificationStates.success
                : notificationStates.warning,
            });
          }

          yield put({
            type: inviteTeamsActionTypes.SEND_INVITE_SUCCESS,
            payload: { success: true, data: messages },
          });
        } else {
          yield put({
            type: inviteTeamsActionTypes.SEND_INVITE_FAILED,
            payload: sendInvitesResponse,
          });
          addNotification({
            message: 'Something went wrong! Please try later.',
            level: notificationStates.error,
          });
        }
      }
    } else if (['Admin', 'Manager', 'Member'].includes(userType)) {
      let allEmails = emails.map((em) => em.toLowerCase());

      // Existed users on the platform
      let existedUsers = yield call(teamService.getAllMembers, allEmails);
      let existedEmails = existedUsers.map((usr) => usr.accountEmailid);

      // Non-existed users on the platform
      let nonExistedEmails = allEmails.filter(
        (em) => !existedEmails.includes(em)
      );

      // Gets team data to verify eligibility of the new users
      let { success, data: teamsData } = yield call(
        teamService.getAllTeamMembers,
        {
          teams: ['Manager', 'Admin'].includes(userType)
            ? managingTeams
            : memberOfTeams,
          authUser: sender?.userId,
          role: sender?.role,
        }
      );
      let teamsIDs = (teamsData ?? []).map((i) => ({
        teamId: i.team,
        teamName: i.teamName,
      }));
      // finds the existed users on the platform & eligible to join the team
      let existedEligibleUsersData = yield call(
        teamService.getExistedEligibleUsers,
        existedUsers,
        teamsData,
        teamsIDs,
        userType
      );
      // finalizes the data to add into the inviteDatabase
      let finalizeInvitationData = [];
      if (userType !== 'Admin') {
        finalizeInvitationData = yield call(
          teamService.getFinalizeInvitationData,
          existedUsers,
          allEmails,
          teamsIDs,
          existedEligibleUsersData,
          sender,
          userType
        );
      } else {
        finalizeInvitationData = yield call(
          getFinalizeAdminData,
          existedEligibleUsersData,
          nonExistedEmails,
          managingTeams,
          sender
        );
      }

      if (finalizeInvitationData && existedEligibleUsersData) {
        const sendInvitesResponse = yield call(
          [teamService, teamService.addInvitesToDatabase],
          finalizeInvitationData,
          userType
        );
        if (sendInvitesResponse.success) {
          const messages = sendInvitesResponse.data || [];

          for (const msg of messages) {
            addNotification({
              message: `${msg.email}: ${msg.message}`,
              level: msg.message.toLowerCase().includes('sent')
                ? notificationStates.success
                : notificationStates.warning,
            });
          }
          yield put({
            type: inviteTeamsActionTypes.SEND_INVITE_SUCCESS,
            payload: { success: true, data: messages },
          });
        } else {
          yield put({
            type: inviteTeamsActionTypes.SEND_INVITE_FAILED,
            payload: sendInvitesResponse,
          });
          addNotification({
            message: 'Something went wrong! Please try later.',
            level: notificationStates.error,
          });
        }
      }
    } else {
      yield put({
        type: inviteTeamsActionTypes.SEND_INVITE_FAILED,
        payload: {
          success: false,
          data: 'Something went wrong! Please try later.',
        },
      });
      addNotification({
        message: 'Something went wrong! Please try later.',
        level: notificationStates.error,
      });
      yield put({ type: inviteTeamsActionTypes.SEND_INVITE_CLEANUP });
    }
  } catch (e) {
    addNotification({
      message: 'Something went wrong! Please try later.',
      level: notificationStates.error,
    });
    yield put({ type: inviteTeamsActionTypes.SEND_INVITE_FAILED, payload: e });
  }
  yield put({ type: inviteTeamsActionTypes.SEND_INVITE_CLEANUP });
}

function* getMembersSaga({ payload }) {
  yield put({ type: manageTeamMembersActionTypes.GET_MEMBERS_PENDING });
  try {
    let { success, data } = yield call(
      manageTeamsService.getAllTeamMembersData,
      {
        ...payload,
      }
    );
    if (success) {
      yield put({
        type: manageTeamMembersActionTypes.GET_MEMBERS_SUCCESS,
        payload: { success: true, data: [...data] },
      });
    } else {
      yield put({
        type: manageTeamMembersActionTypes.GET_MEMBERS_FAILED,
        payload: { success: false, data: 'No data available' },
      });
    }
  } catch (e) {
    yield put({
      type: manageTeamMembersActionTypes.GET_MEMBERS_FAILED,
      payload: e,
    });
  }
}

function* updateEmployeeDetailsSaga({ payload }) {
  yield put({
    type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS_PENDING,
  });
  try {
    const result = yield call(
      manageTeamsService.updateEmployeeDetails,
      payload
    );
    if (result.success) {
      yield put({
        type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS_SUCCESS,
        payload: result,
      });
    } else {
      yield put({
        type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS_FAILED,
        payload: { message: result.message },
      });
    }
  } catch (e) {
    yield put({
      type: manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS_FAILED,
      payload: { message: e.message || 'Failed to update employee details' },
    });
  }
}

function* removeUserFromTeamSaga({ payload }) {
  yield put({
    type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM_PENDING,
  });
  try {
    const result = yield call(manageTeamsService.removeUsers, payload);
    if (result.success) {
      yield put({
        type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM_SUCCESS,
        payload: result,
      });
    } else {
      yield put({
        type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM_FAILED,
        payload: { message: result.message },
      });
    }
  } catch (e) {
    yield put({
      type: manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM_FAILED,
      payload: { message: e.message || 'Failed to remove user from team' },
    });
  }
}

function* getTeamsSaga({ payload }) {
  yield put({ type: manageTeamsActionTypes.GET_TEAMS_PENDING });
  try {
    let { success, data } = yield call(teamService.getAllTeamMembers, {
      ...payload,
    });
    if (success) {
      yield put({
        type: manageTeamsActionTypes.GET_TEAMS_SUCCESS,
        payload: { success: true, data: [...data] },
      });
    } else {
      yield put({
        type: manageTeamsActionTypes.GET_TEAMS_FAILED,
        payload: { success: false, data: 'No data available' },
      });
    }
  } catch (e) {
    yield put({ type: manageTeamsActionTypes.GET_TEAMS_FAILED, payload: e });
  }
}

function* manageTeamsWatcher() {
  yield all([takeEvery(manageTeamsActionTypes.GET_TEAMS, getTeamsSaga)]);
}
function* inviteTeamsWatcher() {
  yield all([takeEvery(inviteTeamsActionTypes.SEND_INVITE, sendInviteSaga)]);
}

function* manageTeamMembersWatcher() {
  yield all([
    takeEvery(manageTeamMembersActionTypes.GET_MEMBERS, getMembersSaga),
    takeEvery(
      manageTeamMembersActionTypes.UPDATE_EMPLOYEE_DETAILS,
      updateEmployeeDetailsSaga
    ),
    takeEvery(
      manageTeamMembersActionTypes.REMOVE_USER_FROM_TEAM,
      removeUserFromTeamSaga
    ),
  ]);
}
export default function* manageTeamsRootWatcher() {
  yield all([
    fork(inviteTeamsWatcher),
    fork(manageTeamMembersWatcher),
    fork(manageTeamsWatcher),
  ]);
}
